import { Nullable } from "common/types";
import { fromJS, Record, Map, List } from "immutable";
import {
  customFormApplicantFromJSON,
  DabellaCustomFormApplicantRecord,
  customFormApplicantToJSON,
  IDabellaCustomFormApplicantData,
} from "common/records/DabellaCustomFormApplicantRecord";

export const customFormFromJSON = (json: Partial<IDabellaCustomFormData>): DabellaCustomFormRecord => {
  const data: IDabellaCustomFormRecord = { ...(json as any) };

  if (json.applicant) {
    data.applicant = customFormApplicantFromJSON(json.applicant);
  }

  if (json.co_applicant) {
    data.co_applicant = customFormApplicantFromJSON(json.co_applicant);
  }

  if (json.form_data) {
    data.form_data = fromJS(json.form_data);
  }

  // TODO: Consider adding `checkDate` for `created_at` and `updated_at` fields

  return new DabellaCustomFormRecord(data);
};

export const customFormToJSON = (record: DabellaCustomFormRecord): any => {
  const customFormData = record.toJS() as any;

  const permittedData = {
    kind: customFormData.kind,
    job_uuid: customFormData.job_uuid,
    form_data: customFormData.form_data,
  } as any;
  if (record.id !== -1) {
    permittedData.id = record.id;
  }

  return permittedData;
};

export const dabellaCustomFormToJSON = (record: DabellaCustomFormRecord): any => {
  const customFormData = record.toJS() as any;

  if (customFormData.id === -1) {
    delete customFormData.id;
  }

  if (record.applicant) {
    customFormData.applicant = customFormApplicantToJSON(record.applicant, "applicant");
  }
  if (record.co_applicant) {
    customFormData.co_applicant = customFormApplicantToJSON(record.co_applicant, "co_applicant");
  }

  customFormData.product_roof = record.products?.includes("roof") ?? false;
  customFormData.product_siding = record.products?.includes("siding") ?? false;
  customFormData.product_gutters = record.products?.includes("gutters") ?? false;
  customFormData.product_windows = record.products?.includes("windows") ?? false;
  customFormData.product_bath = record.products?.includes("bath") ?? false;
  delete customFormData.products;

  customFormData.todays_date = customFormData.todays_date?.toISOString().split("T")[0];
  customFormData.property_purchase_date = customFormData.property_purchase_date?.toISOString().split("T")[0];

  delete customFormData.form_data;
  delete customFormData.loading;
  delete customFormData.created_at;
  delete customFormData.updated_at;

  return customFormData;
};

export type CustomFormKind = "pii_form" | "job_resulting";

export interface IDabellaCustomFormData {
  id: string | number;
  kind: CustomFormKind;
  job_uuid: string;
  form_data: any;
  applicant: Partial<IDabellaCustomFormApplicantData>;
  co_applicant: Partial<IDabellaCustomFormApplicantData>;

  todays_date: Nullable<Date>;
  market: string;
  rep_1: string;
  rep_2: string;
  customer_name: string;
  products: List<string>;
  product_roof: boolean;
  product_siding: boolean;
  product_gutters: boolean;
  product_windows: boolean;
  product_bath: boolean;
  proof_of_income: string;
  finance_information: string;
  special_plan: string;
  deposit: Nullable<number>;
  credit_select: Nullable<string>;
  county: string;
  estimated_payment: Nullable<number>;
  credit_score: Nullable<number>;
  plan: Nullable<string>;
  standard_apr: Nullable<string>;
  standard_term: Nullable<string>;
  deferred_term: Nullable<string>;
  amount: Nullable<number>;
  purpose: Nullable<string>;
  dealer_name: Nullable<string>;

  property_purchase_date: Nullable<Date>;
  property_payment_rent: Nullable<number>;
  property_balance_owed: Nullable<number>;
  property_market_value: Nullable<number>;
  property_home_type: Nullable<string>;
  property_primary_residence: Nullable<string>;
  property_owner: Nullable<string>;
  property_second_mortgage: Nullable<string>;
  property_second_mortgage_payment: Nullable<number>;
  property_second_mortgage_balance: Nullable<number>;
  application_status: Nullable<string>;

  created_at: Nullable<string>;
  updated_at: Nullable<string>;
  loading: boolean;
}

export interface IDabellaCustomFormRecord {
  id: string | number;
  job_uuid: string;
  kind: CustomFormKind;
  form_data: any;
  applicant: DabellaCustomFormApplicantRecord;
  co_applicant: DabellaCustomFormApplicantRecord;

  todays_date: Nullable<Date>;
  market: string;
  rep_1: string;
  rep_2: string;
  customer_name: string;
  products: List<string>;
  proof_of_income: string;
  finance_information: string;
  special_plan: string;
  deposit: Nullable<number>;

  credit_select: Nullable<string>;
  county: string;
  estimated_payment: Nullable<number>;
  credit_score: Nullable<number>;
  plan: Nullable<string>;
  standard_apr: Nullable<string>;
  standard_term: Nullable<string>;
  deferred_term: Nullable<string>;
  amount: Nullable<number>;
  purpose: Nullable<string>;
  dealer_name: Nullable<string>;

  property_purchase_date: Nullable<Date>;
  property_payment_rent: Nullable<number>;
  property_balance_owed: Nullable<number>;
  property_market_value: Nullable<number>;
  property_home_type: Nullable<string>;
  property_primary_residence: Nullable<string>;
  property_owner: Nullable<string>;
  property_second_mortgage: Nullable<string>;
  property_second_mortgage_payment: Nullable<number>;
  property_second_mortgage_balance: Nullable<number>;
  application_status: Nullable<string>;

  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
  loading: boolean;
}

const defaultCustomFormProps: IDabellaCustomFormRecord = {
  id: -1,
  kind: "pii_form",
  job_uuid: "",
  form_data: Map(),
  todays_date: new Date(),
  market: "",
  rep_1: "",
  rep_2: "",
  customer_name: "",
  applicant: customFormApplicantFromJSON({}),
  co_applicant: customFormApplicantFromJSON({}),
  products: List<string>(),
  proof_of_income: "",
  finance_information: "",
  special_plan: "",
  deposit: null,

  credit_select: null,
  county: "",
  estimated_payment: null,
  credit_score: null,
  plan: null,
  standard_apr: null,
  standard_term: null,
  deferred_term: null,
  amount: null,
  purpose: null,
  dealer_name: null,

  property_purchase_date: null,
  property_payment_rent: null,
  property_balance_owed: null,
  property_market_value: null,
  property_home_type: null,
  property_primary_residence: null,
  property_owner: null,
  property_second_mortgage: null,
  property_second_mortgage_payment: null,
  property_second_mortgage_balance: null,
  application_status: null,

  created_at: null,
  updated_at: null,
  loading: false,
};

export class DabellaCustomFormRecord extends Record(defaultCustomFormProps) implements IDabellaCustomFormRecord {}

export const PRODUCT_OPTIONS = ["roof", "siding", "gutters", "windows", "bath"];

export const dabellaCustomFormTestData: Partial<IDabellaCustomFormData> = {
  id: 2,
  applicant: {
    first_name: "John",
    middle_initial: "D",
    last_name: "Doe",
    marital_status: "UNMARRIED",
    us_citizen: "YES",
    physical_address: {
      line_1: "123 Main St",
      line_2: "PO Box 123",
      city: "Anytown",
      state: "Indiana",
      postal_code: "12345",
    },
    mailing_address: {
      same_as_physical: true,
      line_1: "123 Main St",
      line_2: "PO Box 123",
      city: "Anytown",
      state: "Indiana",
      postal_code: "12345",
    },
    picture_id_expiration_date: new Date("2024-01-03"),
    picture_id_issue_date: new Date("2024-01-04"),
    phone: "123-456-7890",
    email: "john.doe@example.com",
    gross_monthly_income: 100,
    employed_start_date: new Date("2024-01-08"),
    employer_name: "OCC",
    employer_phone: "123-456-7890",
    position: "Sales",
    previous_employer: "OCC1",
    less_than_2_years: "YES",
    previous_employer_start_date: new Date("2024-01-10"),
    previous_employer_end_date: new Date("2024-01-11"),
    additional_income_from: new Date("2024-01-09"),
    additional_income_amount: 100,
    additional_income_source: "Random",
  },
  co_applicant: {
    first_name: "Jane",
    middle_initial: "D",
    last_name: "Doe",
    marital_status: "MARRIED",
    us_citizen: "YES",
    physical_address: {
      same_as_applicant: false,
      line_1: "123 Main St",
      line_2: "PO Box 123",
      city: "Anytown",
      state: "Indiana",
      postal_code: "12345",
    },
    mailing_address: {
      same_as_physical: false,
      line_1: "123 Main St",
      line_2: "PO Box 123",
      city: "Anytown",
      state: "Indiana",
      postal_code: "12345",
    },
    picture_id_expiration_date: new Date("2024-01-05"),
    picture_id_issue_date: new Date("2024-01-06"),
    phone: "123-456-7890",
    email: "jane.doe@example.com",
    gross_monthly_income: 100,
    employed_start_date: new Date("2024-01-08"),
    employer_name: "OCC",
    employer_phone: "123-456-7890",
    position: "Sales",
    less_than_2_years: "YES",
    previous_employer: "OCC1",
    previous_employer_start_date: new Date("2024-01-10"),
    previous_employer_end_date: new Date("2024-01-11"),
    additional_income_from: new Date("2024-01-09"),
    additional_income_amount: 100,
    additional_income_source: "Random",
  },
  todays_date: new Date("2024-01-01"),
  market: "SAMPLE MARKET",
  rep_1: "SAMPLE REP 1",
  rep_2: "SAMPLE REP 2",
  customer_name: "SAMPLE CUSTOMER",
  products: List<string>(["roof"]),
  proof_of_income: "YES",
  finance_information: "SAMPLE FINANCE INFORMATION",
  special_plan: "SAMPLE SPECIAL PLAN",
  deposit: 1000,
  property_purchase_date: new Date("2024-01-02"),
  credit_select: "GOOD",
  county: "SAMPLE COUNTY",
  estimated_payment: 200,
  credit_score: 700,
  plan: "STANDARD",
  standard_apr: "9.99",
  standard_term: "36",
  deferred_term: "3",
  amount: 5000,
  purpose: "HOME IMPROVEMENT",
  dealer_name: "SAMPLE DEALER",

  property_payment_rent: 1200,
  property_balance_owed: 30000,
  property_market_value: 50000,
  property_home_type: "1-4 FAMILY",
  property_primary_residence: "YES",
  property_owner: "YES",
  property_second_mortgage: "YES",
  property_second_mortgage_payment: 100,
  property_second_mortgage_balance: 20000,

  created_at: null,
  updated_at: null,
  loading: false,
};
